import { useState, useEffect } from "react";
import { Switch, Route } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, ShieldCheck, Lock, Anchor } from "lucide-react";
import StampForm from "@/components/stamp-form";

// Import your pages
import Dashboard from "@/pages/dashboard";
import IdentityPage from "@/pages/identity"; 
import PassportPage from "@/pages/passport";
import Booklet from "@/pages/booklet";

const queryClient = new QueryClient();

// --- 2FA CHALLENGE COMPONENT ---
function MfaVerification({ onVerified }: { onVerified: () => void }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const factorId = factors?.totp?.[0]?.id;
      if (!factorId) throw new Error("No MFA factor found.");

      const { data: challenge } = await supabase.auth.mfa.challenge({ factorId });
      if (!challenge) throw new Error("Failed to create challenge.");

      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });

      if (error) throw error;
      onVerified();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-white p-6 pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-sm w-full space-y-8 text-center animate-in fade-in zoom-in-95 duration-500">
        <Lock className="w-10 h-10 mx-auto mb-4 opacity-20" />
        <h1 className="font-serif text-2xl uppercase tracking-widest">Netport Identity</h1>
        <p className="font-mono text-[10px] uppercase tracking-widest opacity-40">Challenge required for node synchronization</p>
        <form onSubmit={handleVerify} className="space-y-4">
          <Input 
            placeholder="000000" 
            className="rounded-none h-14 border-black text-center text-2xl tracking-[0.5em] font-mono focus:ring-0" 
            value={code} 
            onChange={(e) => setCode(e.target.value)} 
            maxLength={6}
            required 
          />
          <Button type="submit" disabled={loading} className="w-full bg-black text-white rounded-none h-12 font-serif uppercase tracking-widest">
            {loading ? <Loader2 className="animate-spin h-4 w-4" /> : "Verify Identity"}
          </Button>
        </form>
        <button onClick={() => supabase.auth.signOut()} className="font-mono text-[9px] uppercase tracking-widest opacity-30 hover:opacity-100 transition-opacity">
          Disconnect Session
        </button>
      </div>
    </div>
  );
}

// --- STANDARD LOGIN GATE ---
function AuthGate() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    await supabase.auth.signInWithPassword({ email, password });
    setAuthLoading(false);
  };

  return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-white p-6 pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-sm w-full space-y-8">
        <div className="text-center space-y-4">
          <div className="w-14 h-14 border border-black mx-auto flex items-center justify-center">
            <Anchor className="w-6 h-6" />
          </div>
          <h1 className="font-serif text-3xl tracking-tighter uppercase">Netport Control</h1>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <Input type="email" placeholder="NODE_ID" className="rounded-none h-12 border-slate-200 font-serif text-[10px] tracking-widest uppercase" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input type="password" placeholder="SECRET_KEY" className="rounded-none h-12 border-slate-200 font-serif text-[10px] tracking-widest uppercase" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <Button type="submit" disabled={authLoading} className="w-full bg-black text-white rounded-none h-14 font-serif uppercase tracking-[0.2em]">
            {authLoading ? <Loader2 className="animate-spin h-4 w-4 mx-auto" /> : "Initiate Connection"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [isAal2, setIsAal2] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const checkMfa = async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setIsAal2(data?.currentLevel === 'aal2');
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) checkMfa();
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) checkMfa();
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return null;
  if (!session) return <AuthGate />;
  if (!isAal2) return <MfaVerification onVerified={() => setIsAal2(true)} />;

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SidebarProvider defaultOpen={true}>
          <div className="flex h-screen w-full bg-[#fafafa]">
            <AppSidebar />
            <div className="flex-1 flex flex-col min-w-0">
              {/* Header updated with iOS Safe Area padding */}
              <header className="px-6 md:px-8 pt-[env(safe-area-inset-top)] border-b border-black/10 bg-white flex flex-col justify-center shrink-0 min-h-[calc(64px+env(safe-area-inset-top))]">
                <div className="h-16 flex justify-between items-center w-full">
                  <div className="flex items-center gap-4">
                    <SidebarTrigger />
                    <div className="h-4 w-[1px] bg-black/10" />
                    <span className="font-serif text-[10px] uppercase tracking-[0.4em] text-black/40">Netport Terminal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="hidden sm:flex items-center gap-2 px-3 py-1 border border-emerald-500/20 bg-emerald-50 rounded-full mr-2">
                      <ShieldCheck className="w-3 h-3 text-emerald-600" />
                      <span className="text-[8px] font-mono text-emerald-600 uppercase tracking-tighter">Secure Link</span>
                    </div>
                    <Button 
                      onClick={() => setIsFormOpen(true)}
                      className="bg-black text-white rounded-none px-4 md:px-6 h-9 md:h-10 gap-2 hover:bg-zinc-800 transition-all font-serif uppercase text-[10px] tracking-widest"
                    >
                      <Plus className="w-4 h-4" />Stamp
                    </Button>
                  </div>
                </div>
              </header>
              
              <main className="flex-1 overflow-auto bg-white pb-[env(safe-area-inset-bottom)]">
                <Switch>
                  <Route path="/" component={Dashboard} /> 
                  <Route path="/booklet" component={Booklet} />
                  <Route path="/identity" component={IdentityPage} />
                  <Route path="/passport" component={PassportPage} />
                </Switch>
              </main>
            </div>
          </div>
          <StampForm open={isFormOpen} onClose={() => setIsFormOpen(false)} />
          <Toaster />
        </SidebarProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}